import { Graphics } from 'pixi.js';

/**
 * Lightweight particle system for visual effects (hit sparks, death bursts, etc.).
 * Particles are small colored squares that move, fade, and die.
 */
export class ParticleSystem {
  constructor(worldContainer) {
    this.worldContainer = worldContainer;
    this.particles = [];
  }

  /**
   * Spawn a burst of particles at a position.
   * @param {number} x - World X center
   * @param {number} y - World Y center
   * @param {object} opts - Particle options
   * @param {number} opts.color - Particle color
   * @param {number} [opts.count=6] - Number of particles
   * @param {number} [opts.speed=40] - Max initial speed
   * @param {number} [opts.size=2] - Particle size in pixels
   * @param {number} [opts.life=0.4] - Lifetime in seconds
   * @param {number} [opts.gravity=0] - Downward acceleration
   */
  burst(x, y, opts) {
    const count = opts.count || 6;
    const speed = opts.speed || 40;
    const size = opts.size || 2;
    const life = opts.life || 0.4;
    const gravity = opts.gravity || 0;
    const color = opts.color || 0xffffff;

    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.5;
      const spd = speed * (0.5 + Math.random() * 0.5);
      const vx = Math.cos(angle) * spd;
      const vy = Math.sin(angle) * spd;

      const gfx = new Graphics();
      gfx.rect(-size / 2, -size / 2, size, size);
      gfx.fill(color);
      gfx.x = x;
      gfx.y = y;
      this.worldContainer.addChild(gfx);

      this.particles.push({ gfx, x, y, vx, vy, age: 0, life, gravity });
    }
  }

  /**
   * Spawn impact sparks (directional, away from source).
   * @param {number} x - Hit position X
   * @param {number} y - Hit position Y
   * @param {number} dirX - Direction X (normalized)
   * @param {number} dirY - Direction Y (normalized)
   * @param {number} color - Particle color
   */
  impact(x, y, dirX, dirY, color) {
    const count = 4;
    for (let i = 0; i < count; i++) {
      const spread = (Math.random() - 0.5) * 1.2;
      const spd = 30 + Math.random() * 30;
      const cos = dirX * Math.cos(spread) - dirY * Math.sin(spread);
      const sin = dirX * Math.sin(spread) + dirY * Math.cos(spread);

      const gfx = new Graphics();
      gfx.rect(-1, -1, 2, 2);
      gfx.fill(color);
      gfx.x = x;
      gfx.y = y;
      this.worldContainer.addChild(gfx);

      this.particles.push({
        gfx, x, y,
        vx: cos * spd,
        vy: sin * spd,
        age: 0,
        life: 0.25,
        gravity: 0,
      });
    }
  }

  update(dt) {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.age += dt;

      if (p.age >= p.life) {
        this.worldContainer.removeChild(p.gfx);
        p.gfx.destroy();
        this.particles.splice(i, 1);
        continue;
      }

      p.vy += p.gravity * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.gfx.x = Math.round(p.x);
      p.gfx.y = Math.round(p.y);
      p.gfx.alpha = 1 - (p.age / p.life);
    }
  }

  destroy() {
    for (const p of this.particles) {
      this.worldContainer.removeChild(p.gfx);
      p.gfx.destroy();
    }
    this.particles.length = 0;
  }
}
